import {
  type SortDir,
  type SortValue,
  SortableHead,
  type UseTableSort,
  compareValues,
  useTableSort,
} from "@/components/ui/sortable-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { rowClickProps } from "@/lib/row-selection";
import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import type * as React from "react";
import { Fragment, useMemo } from "react";

/**
 * The one table to rule the list views. Column-definition driven, thin
 * over the existing primitives (`table.tsx`, `sortable-table.tsx`) —
 * it exists so every list stops hand-assembling the same wrapper div,
 * header row, body map, empty-state row and colSpan arithmetic.
 *
 * What it standardises:
 *
 *   • Sorting — a column with `sortAccessor` gets a `SortableHead`.
 *     Pass `sort` (from `useUrlTableSort`) for URL/server-driven
 *     ordering; omit it and the table sorts client-side on its own.
 *   • Conditional columns — `hidden: true` drops the column AND keeps
 *     the empty-state colSpan correct, no more `isAdmin ? 8 : 7`.
 *   • Empty state — one style, one place; pass the copy per table
 *     ("No users yet.").
 *   • Whole-row navigation (`rowLink`) — SPA navigate on a click that
 *     didn't hit an interactive element (same guard-selector the
 *     multi-select lists use), `cursor-pointer` affordance included.
 *   • Selection (`selection`) — the checkbox column, header
 *     indeterminate state and row-click-to-toggle, matching the
 *     work-items / time-entries pattern. Mutually exclusive with
 *     `rowLink` (a row click can't both navigate and select).
 *   • Density — `compact` absorbs the hand-rolled `px-3 py-2` +
 *     shaded-header look of the ai-spend/agent tables.
 *   • Trees (`tree`) — subtree-stable client sorting for hierarchical
 *     rows (work-items sub-tasks); the caller renders indentation.
 *
 * Deliberately NOT here (yet): inline-edit row machinery — the global
 * time-entries page migrates last, once it can express its inbox rows.
 */

export type DataTableColumn<T> = {
  /** Stable column id — doubles as the sort key. */
  key: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  /** Presence makes the header sortable. */
  sortAccessor?: (row: T) => SortValue;
  /** Extra classes for the `<th>` (width, alignment). */
  headClassName?: string;
  /** Extra classes for every `<td>` in this column. */
  cellClassName?: string | ((row: T) => string | undefined);
  /** Conditionally drop the column — colSpan math stays correct. */
  hidden?: boolean;
};

type Selection<T> = {
  selected: Set<string>;
  onToggle: (id: string) => void;
  /** Toggle-all over the currently visible selectable rows. */
  onToggleAll: (visibleIds: string[]) => void;
  /** Accessible label for a row's checkbox. */
  rowLabel?: (row: T) => string;
  /** Accessible label for the header checkbox (default "Select all"). */
  allLabel?: string;
  /** Rows excluded from selection (e.g. discarded inbox rows) — they
   *  don't count towards the header checkbox state. */
  selectable?: (row: T) => boolean;
};

type DataTableProps<T> = {
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  /**
   * URL/server-driven sort control (`useUrlTableSort`). Omit it and the
   * table sorts client-side using the columns' `sortAccessor`s.
   */
  sort?: Pick<UseTableSort<unknown>, "sortKey" | "sortDir" | "toggle">;
  /** Empty-state copy ("No users yet."). Defaults to "No records yet." */
  emptyState?: React.ReactNode;
  /** Whole-row SPA navigation target. Exclusive with `selection`. */
  rowLink?: (row: T) => string | null;
  /** Multi-select — checkbox column + row-click-to-toggle. */
  selection?: Selection<T> | undefined;
  rowClassName?: (row: T) => string | undefined;
  /** Semantic `data-testid` for each `<tr>` — keeps e2e locators stable. */
  rowTestId?: (row: T) => string;
  /**
   * Hierarchical rows (sub-tasks under parents). With this set,
   * client-side sorting becomes tree-aware: roots reorder under the
   * active column and each parent's subtree reorders within itself —
   * a flat sort would scatter children away from their parent. The
   * caller still renders the indentation in its own cells.
   */
  tree?: { parentId: (row: T) => string | null };
  /**
   * Escape hatch for special rows: return a fully-rendered `<TableRow>`
   * (it is keyed by DataTable) to replace the default rendering for that
   * row, or `undefined` to fall back to the standard cells. Used for
   * rows whose markup doesn't fit the column model (e.g. the inbox
   * triage rows on the time-entries page).
   */
  renderRow?: (row: T) => React.ReactNode | undefined;
  /** `compact`: dense `px-3 py-2` cells + shaded header. */
  density?: "default" | "compact";
  /** Drop the default `rounded-md border bg-card` wrapper. */
  bare?: boolean;
};

/**
 * Subtree-stable ordering: roots sort under the active column, then each
 * parent's children sort within themselves and follow their parent
 * (depth-first). A row whose parent isn't in the set behaves as a root.
 * Ties fall back to the original index, matching `useTableSort`.
 */
function treeSort<T>(
  rows: T[],
  rowKey: (row: T) => string,
  parentId: (row: T) => string | null,
  accessor: (row: T) => SortValue,
  dir: SortDir,
): T[] {
  const idIndex = new Map(rows.map((r, i) => [rowKey(r), i] as const));
  const childrenByParent = new Map<string, T[]>();
  const roots: T[] = [];
  for (const r of rows) {
    const pid = parentId(r);
    if (pid && idIndex.has(pid)) {
      const arr = childrenByParent.get(pid) ?? [];
      arr.push(r);
      childrenByParent.set(pid, arr);
    } else {
      roots.push(r);
    }
  }
  const cmp = (a: T, b: T): number => {
    const n = compareValues(accessor(a), accessor(b), dir);
    return n !== 0 ? n : (idIndex.get(rowKey(a)) ?? 0) - (idIndex.get(rowKey(b)) ?? 0);
  };
  const out: T[] = [];
  const pushSubtree = (row: T): void => {
    out.push(row);
    const kids = childrenByParent.get(rowKey(row));
    if (!kids) return;
    for (const k of [...kids].sort(cmp)) pushSubtree(k);
  };
  for (const r of [...roots].sort(cmp)) pushSubtree(r);
  return out;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  sort,
  emptyState = "No records yet.",
  rowLink,
  selection,
  rowClassName,
  rowTestId,
  tree,
  renderRow,
  density = "default",
  bare = false,
}: DataTableProps<T>) {
  const navigate = useNavigate();
  const visible = columns.filter((c) => !c.hidden);

  // Client-side sorting when no external control is supplied. The
  // accessor map is derived from the column defs, so a table gains
  // sorting by adding `sortAccessor` — nothing else.
  const accessors = useMemo(
    () =>
      Object.fromEntries(
        columns.filter((c) => c.sortAccessor).map((c) => [c.key, c.sortAccessor as never]),
      ) as Record<string, (row: T) => SortValue>,
    [columns],
  );
  const clientSort = useTableSort(rows, accessors);
  const control = sort ?? clientSort;
  const sortedRows = useMemo(() => {
    if (sort || !clientSort.sortKey) return rows; // server-ordered / unsorted
    if (!tree) return clientSort.sorted;
    const accessor = accessors[clientSort.sortKey];
    if (!accessor) return rows;
    return treeSort(rows, rowKey, tree.parentId, accessor, clientSort.sortDir);
  }, [
    rows,
    sort,
    tree,
    rowKey,
    accessors,
    clientSort.sortKey,
    clientSort.sortDir,
    clientSort.sorted,
  ]);

  const compact = density === "compact";
  const cellPad = compact ? "px-3 py-2" : undefined;
  const colSpan = visible.length + (selection ? 1 : 0);
  const selectableRows = selection?.selectable ? rows.filter(selection.selectable) : rows;
  const visibleIds = selectableRows.map(rowKey);
  const allChecked = visibleIds.length > 0 && visibleIds.every((id) => selection?.selected.has(id));
  const someChecked = !allChecked && visibleIds.some((id) => selection?.selected.has(id));

  return (
    <div className={cn(!bare && "rounded-md border bg-card")}>
      <Table>
        <TableHeader className={cn(compact && "bg-muted/50")}>
          <TableRow>
            {selection && (
              <TableHead className={cn("w-10", cellPad)}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = someChecked;
                  }}
                  onChange={() => selection.onToggleAll(visibleIds)}
                  className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
                  aria-label={selection.allLabel ?? "Select all"}
                />
              </TableHead>
            )}
            {visible.map((col) =>
              col.sortAccessor ? (
                <SortableHead
                  key={col.key}
                  sortKey={col.key}
                  sort={control}
                  className={col.headClassName}
                  buttonClassName={compact ? "h-9 px-3" : undefined}
                >
                  {col.header}
                </SortableHead>
              ) : (
                <TableHead key={col.key} className={cn(compact && "h-9 px-3", col.headClassName)}>
                  {col.header}
                </TableHead>
              ),
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((row) => {
            const id = rowKey(row);
            const custom = renderRow?.(row);
            if (custom !== undefined) {
              return <Fragment key={id}>{custom}</Fragment>;
            }
            const link = rowLink?.(row) ?? null;
            const clickable = selection
              ? rowClickProps(() => selection.onToggle(id))
              : link
                ? rowClickProps(() => void navigate({ href: link }))
                : null;
            return (
              <TableRow
                key={id}
                data-testid={rowTestId?.(row)}
                data-selected={selection?.selected.has(id) || undefined}
                onClick={clickable?.onClick}
                className={cn(
                  clickable && "cursor-pointer",
                  selection && "data-[selected=true]:bg-primary/5",
                  rowClassName?.(row),
                )}
              >
                {selection && (
                  <TableCell className={cellPad}>
                    <input
                      type="checkbox"
                      checked={selection.selected.has(id)}
                      onChange={() => selection.onToggle(id)}
                      className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
                      aria-label={selection.rowLabel?.(row) ?? `Select row ${id}`}
                    />
                  </TableCell>
                )}
                {visible.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      cellPad,
                      typeof col.cellClassName === "function"
                        ? col.cellClassName(row)
                        : col.cellClassName,
                    )}
                  >
                    {col.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} className="text-center text-muted-foreground py-8">
                {emptyState}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
